import { the_client_app, the_client_version } from '../me/client';
import { $analytics } from '../y/analytics';
import { $http } from '../y/http';
import { $location } from '../y/location';
import { $storage } from '../y/storage';
import { YId, YInstallId, YObjectId } from '../y/types';

declare global {
    interface Window {
        ab_install_id: YInstallId;
    }
}

let track_enabled = false;
let install_id: YInstallId;

let utm_parameters: AUTMParameters | undefined;
const referrer = document.referrer;
const initial_url = location.href;

export type ATrackCode = 'start' | 'end' | 'page' | 'click' | 'share' | 'search';
export type APageCode = 'landing' | 'event' | 'events' | 'past_events' | 'venue' | 'account' | 'billing' | 'bookings' | 'booking' | 'purchases' | 'friends' | 'giftcard' |
    'giftcard_b2b' | 'redeem_code' | 'how' | 'faq' | 'invite' | 'onboarding' | 'producer' | 'signup' | 'tag' | 'subscribe' | 'membership' | 'custom';
export type AElementCode = 'membership_pause' | 'membership_unsubscribe' | 'book_limit';
export type AMediaCode = 'twitter' | 'facebook' | 'email';
export type AShareElement = 'booking' | 'event';

export interface AActionProperties {
    event?: YId;
    booking?: YId;
    session?: YId;
    venue?: YId;
    event_tag?: YId;
    producer?: YId;
    landing_page?: YId;
}

export interface ATrack {
    code: ATrackCode;
    url: string;
    properties: AActionProperties

    page?: APageCode;
    element_code?: AElementCode;
    media?: AMediaCode;
    share_element?: AShareElement;
    query_string?: string;
}

export interface AUTMParameters {
    source: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
}

interface ATrackRequest {
    client: string;
    install: YInstallId;
    tracks: ATrack[];
    width: number;
    height: number;
    referrer: string;
    initial_url: string;
    utm?: AUTMParameters;
}

let queue: ATrack[] | undefined;

function flush() {
    if(!queue || !track_enabled) return;

    const params: ATrackRequest = {
        client: the_client_app + '/' + the_client_version,
        install: install_id,
        tracks: queue,
        width: window.screen.width,
        height: window.screen.height,
        referrer: referrer,
        initial_url: initial_url,
        utm: utm_parameters
    }

    $http.BEACON('/api/v1/track', params);

    queue = undefined;
}

function enqueue(track: ATrack) {
    if(!track_enabled) return;

    if(queue) {
        queue.push(track);
        return;
    }

    const this_queue = queue = [track];

    setTimeout(() => {
        if(this_queue === queue)
            flush();
    }, 1000);
}

export function aInitTrack(enable_tracking: boolean) {
    const stored_id = ($storage.getString('ab_install_id') ?? $storage.getString('tl_install_id')) as YInstallId;

    if(stored_id) {
        install_id = stored_id;
    } else {
        install_id = window.ab_install_id;
        $storage.putString('ab_install_id', install_id, true);
    }

    $http.addGlobalHeader('X-Abundo-Install', install_id);

    const query = $location.getCurrentQuery();

    if(query.utm_source) {
        utm_parameters = {
            source: query.utm_source,
            medium: query.utm_medium,
            campaign: query.utm_campaign,
            term: query.utm_term,
            content: query.utm_content
        };
    }

    if(enable_tracking) {
        track_enabled = true;

        aTrack('start');
        flush();

        window.addEventListener('pagehide', flush);

        window.addEventListener('beforeunload', () => {
            aTrack('end');
            flush();
        });
    }
}

export function aTrack(code: ATrackCode, properties: AActionProperties = {}) {
    if(!track_enabled) return;

    enqueue({
        code: code,
        url: window.location.href,
        properties: properties
    });
}

export function aTrackClick(element: AElementCode, properties: AActionProperties = {}) {
    if(!track_enabled) return;

    enqueue({
        code: 'click',
        url: window.location.href,
        properties: properties,

        element_code: element
    })
}

export function aTrackSearch(query: string) {
    if(!track_enabled) return;

    enqueue({
        code: 'search',
        url: window.location.href,
        properties: {},

        query_string: query
    })
}

export function aTrackShare(what: AShareElement, media: AMediaCode, properties: AActionProperties = {}) {
    if(!track_enabled) return;

    enqueue({
        code: 'share',
        url: window.location.href,
        properties: properties,

        share_element: what,
        media: media
    })
}

export function aTrackPage(page: APageCode, properties: AActionProperties = {}) {
    if(!track_enabled) return;

    enqueue({
        code: 'page',
        url: window.location.href,
        properties: properties,

        page: page
    });

    // let name = page;
    // if(properties.landing_page)
    //     name += ' ' + properties.landing_page;

    $analytics.trackPage();
}

export function aTrackVideo(event_id: YObjectId, video_id: YObjectId, action: 'video.play' | 'video.ended' | 'video.pause' | undefined, played: number, position: number) {
    $http.POST('/api/v1/event/' + event_id + '/video/' + video_id + '/track', { action: action, played: played, position: position })
}
